import { AuthUserType } from 'common/graphql/types';
import { debounce, throttle } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { useOnVisibilityChange } from 'common/hooks/useOnVisibilityChange';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { getCurrentRoleIdDocumentNode } from 'common/auth/queries';
import { AuthActions } from 'common/auth/actions';
import { useTranslation } from 'react-i18next';
import { AuthState } from 'common/auth/types';

const THROTTLE_MS = 10000;
const DEBOUNCE_MS = 12000;

export const useAutoRoleSync = () => {
    const store = useStore<{ auth: AuthState }>();

    const checkIfRoleChangedOnBE = useCheckIfRoleChangedOnBE();

    const debouncedCheckIfRoleChangedOnBe = useCallback(debounce(checkIfRoleChangedOnBE, DEBOUNCE_MS), [
        checkIfRoleChangedOnBE,
    ]);

    const throttledCheckIfRoleChangedOnBe = useCallback(
        throttle(
            () => {
                // call the function one more time in few seconds
                // for when the user comes back to the page too quickly from FX
                debouncedCheckIfRoleChangedOnBe();

                checkIfRoleChangedOnBE();
            },
            THROTTLE_MS,
            { trailing: true }
        ),
        [checkIfRoleChangedOnBE, debouncedCheckIfRoleChangedOnBe]
    );

    const checkIfRoleChangedOnBeWithChecks = useCallback(() => {
        const { activeUser, submitted, switchingUser, userRoleSwitchPending } = store.getState()?.auth || {};

        if (
            !featureFlags.fxIntegration ||
            !activeUser ||
            activeUser.type === AuthUserType.PATIENT ||
            submitted ||
            document.visibilityState !== 'visible' ||
            activeUser.authenticationMethod !== 'OKTA' ||
            switchingUser ||
            userRoleSwitchPending
        ) {
            return;
        }

        throttledCheckIfRoleChangedOnBe();
    }, [store, throttledCheckIfRoleChangedOnBe, featureFlags.fxIntegration]);

    useOnVisibilityChange(checkIfRoleChangedOnBeWithChecks);
};

const useCheckIfRoleChangedOnBE = () => {
    const store = useStore<{ auth: AuthState }>();

    const { t } = useTranslation('auth', { useSuspense: false });
    const dispatch = useDispatch();
    const [getRoleInfo] = useLazyQuery(getCurrentRoleIdDocumentNode);

    const resetUserData = useCallback(() => {
        CIPageStatus.warn(t('switch_role.warning.switching_role'));
        dispatch(AuthActions.resetUserData());
    }, [t, dispatch]);

    const checkIfRoleChangedOnBE = useCallback(async () => {
        const activeUser = store.getState()?.auth?.activeUser;
        const { roleVClaim, roleBClaim } = activeUser;

        const { data } = await getRoleInfo();

        const stateRoleClaimSk = roleVClaim?.sk || roleBClaim?.sk;
        const stateRoleClaimFxId = roleVClaim?.fxId || roleBClaim?.fxId;

        const backendRoleClaimSk = data.getUser.roleVClaim?.sk || data.getUser.roleBClaim?.sk;
        const backendRoleClaimFxId = data.getUser.roleVClaim?.fxId || data.getUser.roleBClaim?.fxId;

        const backendRoleChanged =
            stateRoleClaimSk !== backendRoleClaimSk ||
            (stateRoleClaimFxId && stateRoleClaimFxId !== backendRoleClaimFxId);

        const userRoleSwitchPending = store.getState()?.auth?.userRoleSwitchPending;

        if (backendRoleChanged && !userRoleSwitchPending) {
            resetUserData();
        }
    }, [store, getRoleInfo, featureFlags.fxIntegration, resetUserData]);

    return checkIfRoleChangedOnBE;
};
